import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { formatIQD } from "../lib/format";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const toggleDay = (i) => {
    const days = new Set(s.reminder_days || []);
    if (days.has(i)) days.delete(i); else days.add(i);
    setS({ ...s, reminder_days: Array.from(days).sort() });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        reminder_threshold: Number(s.reminder_threshold) || 0,
        reminder_day: Number(s.reminder_day),
        reminder_hour: Number(s.reminder_hour),
        reminder_enabled: !!s.reminder_enabled,
        reminder_message: s.reminder_message,
        twilio_whatsapp_from: s.twilio_whatsapp_from || "",
        cadence: s.cadence || "weekly",
        reminder_days: (s.reminder_days || []).map(Number),
        quiet_hours_enabled: !!s.quiet_hours_enabled,
        quiet_hours_start: Number(s.quiet_hours_start),
        quiet_hours_end: Number(s.quiet_hours_end),
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Account details and payment reminder configuration.</p>
      </div>

      <Card className="p-5 space-y-2">
        <div className="heading font-bold mb-1">Account</div>
        <div><div className="text-xs uppercase text-muted-foreground">Signed in as</div><div className="font-medium">{user?.name} · {user?.email}</div></div>
        <div><div className="text-xs uppercase text-muted-foreground">Role</div><div className="font-medium capitalize">{user?.role}</div></div>
        <div><div className="text-xs uppercase text-muted-foreground">Currency</div><div className="font-medium">IQD — Iraqi Dinar</div></div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="heading font-bold">WhatsApp Reminders</div>
            <div className="text-xs text-muted-foreground">Automatically remind employees whose outstanding balance is above the threshold.</div>
          </div>
          <Switch checked={!!s.reminder_enabled} onCheckedChange={(v) => setS({ ...s, reminder_enabled: v })} data-testid="rem-enabled" />
        </div>

        <div>
          <Label>Cadence</Label>
          <Select value={s.cadence || "weekly"} onValueChange={(v) => setS({ ...s, cadence: v })}>
            <SelectTrigger className="mt-1.5 w-56" data-testid="rem-cadence"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="twice_weekly">Twice a week</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {s.cadence === "weekly" && (
          <div>
            <Label>Reminder Day (Baghdad time)</Label>
            <Select value={String(s.reminder_day)} onValueChange={(v) => setS({ ...s, reminder_day: Number(v) })}>
              <SelectTrigger className="mt-1.5 w-56" data-testid="rem-day"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {s.cadence === "twice_weekly" && (
          <div>
            <Label>Pick two days (Baghdad time)</Label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {DAYS.map((d, i) => {
                const on = (s.reminder_days || []).includes(i);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i)}
                    data-testid={`rem-day-${i}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white dark:bg-slate-900 border-border hover:border-emerald-500"}`}
                  >
                    {d.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-muted-foreground mt-2">Selected: {(s.reminder_days || []).length}/2 recommended</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Reminder Hour (0–23, Baghdad)</Label>
            <Select value={String(s.reminder_hour)} onValueChange={(v) => setS({ ...s, reminder_hour: Number(v) })}>
              <SelectTrigger className="mt-1.5" data-testid="rem-hour"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 24 }).map((_, i) => <SelectItem key={`h-${i}`} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Outstanding Threshold (IQD)</Label>
            <Input type="number" value={s.reminder_threshold} onChange={(e) => setS({ ...s, reminder_threshold: e.target.value })} className="mt-1.5" data-testid="rem-threshold" />
            <div className="text-xs text-muted-foreground mt-1">Only employees with outstanding &gt; {formatIQD(s.reminder_threshold)} are messaged.</div>
          </div>
        </div>

        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-medium">Quiet Hours</Label>
              <div className="text-xs text-muted-foreground">Skip cron sends during late-night hours. Manual sends are unaffected.</div>
            </div>
            <Switch checked={!!s.quiet_hours_enabled} onCheckedChange={(v) => setS({ ...s, quiet_hours_enabled: v })} data-testid="rem-quiet-enabled" />
          </div>
          {s.quiet_hours_enabled && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quiet from</Label>
                <Select value={String(s.quiet_hours_start)} onValueChange={(v) => setS({ ...s, quiet_hours_start: Number(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="rem-quiet-start"><SelectValue /></SelectTrigger>
                  <SelectContent>{Array.from({ length: 24 }).map((_, i) => <SelectItem key={`qs-${i}`} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quiet until</Label>
                <Select value={String(s.quiet_hours_end)} onValueChange={(v) => setS({ ...s, quiet_hours_end: Number(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="rem-quiet-end"><SelectValue /></SelectTrigger>
                  <SelectContent>{Array.from({ length: 24 }).map((_, i) => <SelectItem key={`qe-${i}`} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <div>
          <Label>Message template</Label>
          <Textarea rows={3} value={s.reminder_message} onChange={(e) => setS({ ...s, reminder_message: e.target.value })} className="mt-1.5" data-testid="rem-message" />
          <div className="text-xs text-muted-foreground mt-1">Use <code className="mono">{"{name}"}</code> and <code className="mono">{"{amount}"}</code> as placeholders.</div>
        </div>

        <div>
          <Label>Twilio WhatsApp sender</Label>
          <Input value={s.twilio_whatsapp_from || ""} onChange={(e) => setS({ ...s, twilio_whatsapp_from: e.target.value })} placeholder="+14155238886 or your approved WABA number" className="mt-1.5 mono" data-testid="rem-from" />
          <div className="text-xs text-muted-foreground mt-1">Your Twilio Account SID and Auth Token are configured in server env.</div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button onClick={save} disabled={saving} data-testid="btn-save-settings" className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
